import { FormEvent, memo, useContext } from "react";
import NewAbsorptionsSelect from "./NewAbsorptionsSelect";
import AddToCart from "./AddToCart";
import NewSizes from "./NewSizes";
import { useLocale, useTranslations } from "next-intl";
import QuantityControlContainer from "./QuantityControlContainer";
import { useRouter } from "next/router";
import EventTracker from "src/helpers/event-tracking/eventTrackingManager";
import useCountryData from "src/hooks/useCountryData";
import addParamsToQuery from "src/helpers/router-helpers/addParamsToQuery";
import useSummaryDetails from "src/hooks/useSummaryDetails";
import ColorPicker from "src/components/colors/ColorPicker";
import { KoalaProduct } from "src/types/koala/KoalaProduct";
import { KoalaVariant } from "src/types/koala/KoalaVariant";
import { useStoreActions, useStoreState } from "src/hooks/storeHooks";
import useAddToCartQuantity from "src/hooks/useAddToCartQuantity";
import koalaUtils from "src/helpers/koala/koalaUtils";
import { KoalaCart, KoalaInsertCart, KoalaInsertLineItem } from "src/types/koala/KoalaCart";
import Country from "src/helpers/localization-helpers/countryClass";
import useNeverthrowAsync from "src/hooks/useNeverthrowAsync";
import { matchesProperty, negate, property } from "lodash";
import { useUpdateEffect } from "usehooks-ts";
import { propertyIn } from "src/helpers/fp/fpUtils";
import useCartAttributes from "src/hooks/useCartAttributes";
import { Locale } from "src/types/localization/Locale";
import { PaymentContext } from "src/context/payment";
import { toast } from "react-toastify";

export interface GiftCardFormValues {
  receiverName: string;
  receiverEmail: string;
  message: string;
}

export interface GiftCardFormErrors {
  receiverName: string;
  receiverEmail: string;
}

interface KoalaAddToCartFormProps {
  product: KoalaProduct;
  variant: KoalaVariant;
  stickyFooter: boolean;
}

function KoalaAddToCartForm({ product, variant, stickyFooter }: KoalaAddToCartFormProps) {
  /**
   * States
   */
  const router = useRouter();
  const t = useTranslations("ProductOverview");
  const countryData = useCountryData();
  const locale = useLocale() as Locale;
  // cart
  const cart = useStoreState((state) => state.cart);
  const { deliveryOption } = useContext(PaymentContext);
  const cartAttrtibutes = useCartAttributes();
  const createCartThunk = useStoreActions((state) => state.createCartThunk);
  const updateCartItemsThunk = useStoreActions((state) => state.updateCartItemsThunk);
  // quantity
  const { quantity, setQuantity, quantityError, setQuantityError } = useAddToCartQuantity(variant, cart);

  /**
   * Methods
   */
  const [handleCreateCart, createCartLoading] = useNeverthrowAsync(createCartThunk, {
    uiSuccessMessage: `Successfully, created new cart with ${variant.name}`,
    uiErrorMessage: `Something went wrong while creating your cart`,
  });

  const [handleupdateCartItems, updateCartLoading] = useNeverthrowAsync(updateCartItemsThunk, {
    uiSuccessMessage: `Successfully, updated your cart`,
    uiErrorMessage: `Something went wrong while updating your cart`,
  });

  function handleSubmit({
    product,
    cart,
    variant,
    quantity,
    countryData,
    locale,
  }: {
    product: KoalaProduct;
    cart: KoalaCart | null;
    variant: KoalaVariant;
    quantity: "" | number;
    countryData: Country;
    locale: Locale;
  }) {
    return async function (event: FormEvent<HTMLFormElement>) {
      event.preventDefault();

      // validate quantity
      if (quantity == "" || quantity <= 0) {
        return setQuantityError(t("min_quantity_message"));
      }
      const variantFromCartQuantity = cart?.lineItems.find(matchesProperty("variantId", variant._id))?.quantity || 0;
      const isOutOfStock = quantity + variantFromCartQuantity > variant.quantity;
      if (isOutOfStock) {
        toast.error(t("item_out_of_stock"), { position: "bottom-right", icon: false });
        return;
      }

      EventTracker.addToKoalaCart(product, Number(quantity));
      // Case 1: Cart doesn't exist => create cart
      if (!cart) {
        const cartResult = await handleCreateCart({
          lineItems: [{ variantId: variant._id, sku: variant.sku, quantity }],
          countryData,
          locale,
          attributes: cartAttrtibutes,
          deliveryMethod: deliveryOption,
        });
        setQuantity(1);
      }

      // Case 2: cart exists => update items
      else {
        const cartResult = await handleupdateCartItems({
          cartId: cart._id,
          newItem: {
            variantId: variant._id,
            sku: variant.sku,
            quantity,
            price: variant.price,
          },
          countryData,
          deliveryMethod: deliveryOption,
        });
        setQuantity(1);
      }
    };
  }

  /**
   * Effects
   */
  useUpdateEffect(
    function triggerPageViewedEvent() {
      if (product) {
        EventTracker.koalaProductViewed(product);
      }
    },
    [product]
  );

  /**
   * Get options values with their stock
   */
  const absorptionValuesWithStock = koalaUtils.getOptionValuesWithStock(product, "Absorption");
  const colorValuesWithStock = koalaUtils.getOptionValuesWithStock(
    product,
    "Color",
    variant.selectedOptions.filter(matchesProperty("name", "Absorption"))
  );
  const sizeValuesWithStock = koalaUtils.getOptionValuesWithStock(
    product,
    "Size",
    variant.selectedOptions.filter(propertyIn("name", ["Absorption", "Color"]))
  );

  /**
   * Extract selected options from state
   */
  const selectedAbsorption = variant.selectedOptions.find(matchesProperty("name", "Absorption"))?.value || "";
  const selectedColor = variant.selectedOptions.find(matchesProperty("name", "Color"))?.value || "";
  const selectedSize = variant.selectedOptions.find(matchesProperty("name", "Size"))?.value || "";
  const currentVariantInCart = cart ? koalaUtils.getCartItemByVariantId(cart, variant._id) : null;
  const isCartLoading = createCartLoading || updateCartLoading;

  if (!countryData) {
    return <></>;
  }

  return (
    <div>
      <form
        className="flex flex-col gap-12"
        onSubmit={handleSubmit({ product, cart, variant, quantity, countryData, locale })}
      >
        {/* Absorptions */}
        {absorptionValuesWithStock && (
          <div className="shrink-0">
            <NewAbsorptionsSelect
              valuesWithStock={absorptionValuesWithStock}
              selectedAbsorption={selectedAbsorption}
              setSelectedAbsorption={(value) => addParamsToQuery(router, [{ name: "Absorption", value }])}
              stickyFooter={stickyFooter}
            />
          </div>
        )}

        {/* Colors */}
        {colorValuesWithStock && (
          <div className="space-y-2">
            <h2 className="font-bold">{t("choose_color")}</h2>
            <ColorPicker
              selectedColor={selectedColor}
              setSelectedColor={(value) => addParamsToQuery(router, [{ name: "Color", value }])}
              valuesWithStock={colorValuesWithStock}
              circleClassName="!h-8 !w-8"
              optionClassName="!p-1"
            />
          </div>
        )}

        {/* Sizes */}
        {sizeValuesWithStock && (
          <section className="flex flex-col gap-4">
            <div className="flex-1">
              <NewSizes
                valuesWithStock={sizeValuesWithStock}
                selectedSize={selectedSize}
                setSelectedSize={(value) => addParamsToQuery(router, [{ name: "Size", value }])}
                stickyFooter={stickyFooter}
                isTeens={product.handle.includes("waikiki")}
              />
            </div>
          </section>
        )}

        {/* Quantity controller and add to cart button */}
        <section className="space-y-2.5">
          <div className="flex w-full flex-1 gap-2">
            <QuantityControlContainer
              maxQuantity={variant.quantity}
              quantity={typeof quantity === "number" ? quantity : ""}
              setQuantity={setQuantity}
              disabled={isCartLoading}
              quantityFromCart={currentVariantInCart?.quantity || 0}
              quantityError={quantityError}
              setQuantityError={setQuantityError}
            />
            <AddToCart loading={isCartLoading} disabled={isCartLoading || quantity === ""} />
            {/* {storefrontErrorMessage && <p className="error-message">{storefrontErrorMessage}</p>} */}
            {typeof quantity === "number" && quantity >= 0 && quantityError && (
              <p className="error-message">{quantityError}</p>
            )}
          </div>
        </section>
      </form>
    </div>
  );
}

export default memo(KoalaAddToCartForm);
